import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'app/spacing';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { HistoryModal } from '__components/Modals/HistoryModal';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import { customObjectHistoryQuery } from 'query/custom-object';
import { CustomObjectTimelineEntry } from '@kizen/api/custom-object';
import { HistoryDetailsModal } from 'components/Modals/HistoryModal/HistoryDetailsModal';

type HistoryModalProps = {
  id: string;
  onHide(): void;
};

type ActionOption = {
  value: 'view';
};

const DEFAULT_PAGINATION_SETTINGS = {
  page: 1,
  pageSize: 10,
  ordering: '-created',
};

export const CustomObjectHistoryModal = ({ id, onHide }: HistoryModalProps) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<CustomObjectTimelineEntry | null>(
    null
  );
  const isMobile = useBreakpoint();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { page, pageSize, ordering, search } = pagination;

  const queryParams = {
    page,
    page_size: pageSize,
    ordering,
    search,
  };

  const historyQuery = useQuery(
    customObjectHistoryQuery(id, {
      params: queryParams,
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: ActionOption, data: CustomObjectTimelineEntry) => {
      if (value === 'view') {
        setHistory(data);
      }
    },
    []
  );

  const hideHistoryDetails = useCallback(() => {
    setHistory(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  if (isMobile) {
    return null;
  }

  return (
    <>
      <HistoryModal
        show
        showSearch
        heading={t('Custom Object History Log')}
        onHide={onHide}
        onConfirm={onHide}
        loading={historyQuery.isLoading}
        results={historyQuery.data?.results ?? []}
        columns={columns}
        count={historyQuery.data?.count}
        pagination={pagination}
      />
      {history ? (
        <HistoryDetailsModal data={history.data} onHide={hideHistoryDetails} />
      ) : null}
    </>
  );
};
